/**
 * Bygger søkervisningens datamodell fra motorens `ValidationResult`.
 *
 * Modulen er ren logikk: ingen JSX, ingen React, ingen kobling til søknaden.
 * Den tar imot valideringsresultatet, sertifikatnavnet og hvilke krav søkeren
 * har lastet opp dokumentasjon på, og svarer med et tre presentasjonen bare
 * skal tegne.
 *
 * Modellen er portert fra kraveditoren (SertSlay). Avvikene her er:
 *   - `uploadedFiles` slås opp på kravets `requirementId`, ikke på nodens
 *     posisjonsnøkkel: vedleggene i Min side er EKTE og henger på kravnøkkelen
 *     `certreq:{kode}#{requirementId}`.
 *   - Nodene bærer `requirementId` og det rå `label`-språkkartet, som er det
 *     opplastingen trenger for å bygge kravnøkkel og metadata.
 *   - `engineStatus` bevarer motorens eget svar (pass/fail/inconclusive) som
 *     data. Visningen bruker det ikke — skillet Fail/Inconclusive er ikke noe
 *     søkeren kan handle på — men det skal ikke gå tapt på veien.
 *   - Søsken sorteres på `order` (som i den forrige kravsiden), slik at
 *     kravene står i den rekkefølgen kravdokumentet ber om.
 *   - Tekstene kommer inn ferdig oversatt (`UserViewTexts`) i stedet for å stå
 *     som strenger i modulen.
 *
 * Modellen følger konseptet:
 *   - Basiskravene samles i én felles liste av enkle rader.
 *   - `allOf`-grupper flates inn i lista (én samlet flate, ingen egen ramme).
 *   - `anyOf`/`oneOf`-grupper blir «Minst ett av følgende»-grupper.
 *   - Alternativgruppen løftes ut som egne kort, ett per kvalifiseringsvei.
 *
 * Gjennomgående regel: **samme opplysning skal stå ett sted**. Grupper som
 * bare holder ett krav kollapses, tekst som gjentas mellom to nivåer skrives
 * én gang, og en begrunnelse som bare er en omskriving av den synlige teksten
 * og statusen utelates.
 *
 * Et **enkeltkrav viser beskrivelsen sin** som radens tekst — den er skrevet
 * for søkeren («Kurset må være fullført og gyldig»), mens navnet ofte er
 * kildedataenes eget stikkord («PS_Course_MBH», «GOC and Valid»). Mangler
 * beskrivelsen, står navnet der i stedet, slik at ingen rad blir tom.
 * `nodeText()` er fasiten på hva raden viser, og resten av modulen — kollaps,
 * arv og undertrykking av begrunnelser — måler seg mot den.
 *
 * **Opplastingen følger dokumentasjonsenheten, ikke det enkelte kravet**: en
 * gruppe har én knapp, og kravene under den er forklaringen på hva gruppen ber
 * om. Se «Opplasting: hvem bærer knappen» lenger nede.
 */

import { isDeclarationRequirement, isRequirementConfirmed } from '../declarations';
import { normalizeStatus } from '../labels';
import { sortRequirements } from '../requirementTree';
import { LangMap, NormalizedStatus, RequirementResult, ValidationResult } from '../types';
import { UserViewTexts } from './texts';
import {
  aggregateAll,
  aggregateAny,
  isOutstanding,
  mapEngineStatus,
  UserStatus,
  withUpload,
} from './userStatus';

/* ------------------------------------------------------------------ */
/* Modellen presentasjonen tegner                                       */
/* ------------------------------------------------------------------ */

interface UserNodeBase {
  /** Stabil nøkkel utledet av posisjonen i resultattreet. React-nøkkel. */
  key: string;
  /**
   * Kravets id i kravdokumentet. Den er nøkkelen vedleggene henger på
   * (`certreq:{kode}#{requirementId}`), og finnes også på gruppenoder — en
   * gruppe som dokumenteres under ett trenger sin egen kravnøkkel. Noder uten
   * id kan ikke ta imot dokumentasjon.
   */
  requirementId?: string;
  /** Kravets navn, hentet fra `label`. */
  title: string;
  /**
   * Det rå språkkartet bak `title`. Opplastingen skriver det inn i vedleggets
   * metadata (labelNo/labelEn), og trenger det derfor uoversatt.
   */
  label?: LangMap;
  /**
   * `description` fra kravdokumentet. På et enkeltkrav er den radens synlige
   * tekst (se `nodeText()`); på grupper og alternativer står den under navnet.
   */
  description?: string;
  /** Motorens egen forklaring på hva som mangler. */
  reason?: string;
  /**
   * Kravtypen på norsk («Fartstid», «Sertifikat», «Utdanning»), ment som en
   * **ledetekst foran radteksten**: «Fartstid: 1080 dager som overstyrmann».
   *
   * Feltet er `undefined` når det ikke er noe å si — se `typeLabelFor()` og
   * `groupTypeLabel()` for reglene. Presentasjonen tegner den bare når den
   * finnes, og skal ikke lage sin egen reserve.
   */
  typeLabel?: string;
  status: UserStatus;
  /**
   * Motorens eget svar på noden, uoversatt til søkerens språk.
   *
   * Søkervisningen slår `Fail` og `Inconclusive` sammen til «Mangler», fordi
   * veien videre er den samme. Skillet er likevel ekte informasjon, og bevares
   * her som data — ingen visning bruker det i dag.
   */
  engineStatus?: NormalizedStatus;
  /**
   * Er det denne noden brukeren laster opp dokumentasjon på?
   *
   * Svaret hører hjemme her og ikke i presentasjonen, for det er en regel om
   * modellen: en gruppe dokumenteres under ett, og kravene inni den har ingen
   * egen knapp. Se `assignUpload()` for hele regelen.
   */
  upload: boolean;
  /**
   * Filene som ligger på noden, i den rekkefølgen vedleggstjenesten gir dem.
   *
   * Ett krav dokumenteres sjelden med ett papir: et vitnemål kan bestå av flere
   * sider, og en gruppe som dokumenteres under ett kan trenge ett vedlegg per
   * krav under. Derfor er dette en **liste**, ikke et ja/nei — statusen er
   * fortsatt bare «noe er levert» (`files.length > 0`).
   */
  files: readonly string[];
}

/** Ett krav — én rad. */
export interface UserRequirementNode extends UserNodeBase {
  kind: 'requirement';
  requirementType?: string;
  /** Kan kravet dokumenteres med filopplasting? Utelatt i dataene = true. */
  documentable: boolean;
  /**
   * Er kravet en ERKLÆRING søkeren krysser av for i stedet for å dokumentere?
   *
   * Da tilbys ingen opplasting — heller ikke om kravdokumentet skulle glemme
   * `documentable: false` — og raden bærer en avkryssingsboks i stedet.
   */
  declaration: boolean;
  /**
   * Erklæringen er avgitt på søknaden. Kravet regnes da som **oppfylt**, uansett
   * hva motoren svarte: den vurderer ikke denne kravtypen i det hele tatt.
   */
  confirmed: boolean;
  order?: number;
}

/** Et valg som selv består av flere krav som alle må oppfylles. */
export interface UserBundleNode extends UserNodeBase {
  kind: 'bundle';
  items: UserNode[];
}

/**
 * «Minst ett av følgende» — en lett innrykket gruppe.
 *
 * `title` er gruppens egen tekst (beskrivelsen, eller etiketten når det er alt
 * som finnes), ikke logikken: «Minst ett av» vises ikke. `description` står
 * derfor alltid tom her — teksten er allerede i `title`, på samme linje som
 * statusen.
 */
export interface UserChoiceNode extends UserNodeBase {
  kind: 'choice';
  /** `oneOf` — nøyaktig ett, ikke minst ett. */
  exclusive: boolean;
  options: UserNode[];
}

export type UserNode = UserRequirementNode | UserBundleNode | UserChoiceNode;

/** Én komplett kvalifiseringsvei. */
export interface UserAlternative {
  key: string;
  title: string;
  description?: string;
  status: UserStatus;
  items: UserNode[];
  /** Kravene i alternativet brukeren fortsatt må følge opp. */
  missing: UserRequirementNode[];
  /**
   * Alternativet er ett eneste krav, og kravraden bærer allerede både navnet
   * og statusen: presentasjonen skal tegne raden alene, uten egen topplinje.
   */
  collapsed: boolean;
}

/** Gruppen av alternative kvalifiseringsveier. */
export interface UserAlternativeGroup {
  key: string;
  title: string;
  description?: string;
  status: UserStatus;
  alternatives: UserAlternative[];
  /**
   * Indeksen til veien brukeren er på — den som er oppfylt, eller den hun har
   * levert dokumentasjon på — ellers -1.
   *
   * Både oppfylt og dokumentert peker ut en vei: har brukeren lastet opp det en
   * vei ber om, er det den hun følger, selv om ingen har kontrollert papirene
   * ennå. De øvrige er da ikke lenger noe hun må gjøre noe med, og settes til
   * «Ikke aktuelt». Er ingen vei kommet så langt, står alle åpne.
   */
  highlightIndex: number;
}

export interface UserView {
  certificateName: string;
  /** Samlet status, inkludert opplastingene. */
  status: UserStatus;
  /** Basiskravene — én felles seksjon. */
  baseItems: UserNode[];
  alternativeGroups: UserAlternativeGroup[];
}

export interface BuildUserViewOptions {
  /** Sertifikatets navn, brukt i overskriften. */
  certificateName: string;
  /** Ferdig oversatte tekster. */
  texts: UserViewTexts;
  /** Plukker riktig språk ut av et språkkart. */
  translate: (langMap?: LangMap | null) => string;
  /**
   * Filnavnene som ligger på hvert krav, slått opp på kravets `requirementId`.
   * En id uten oppføring — eller med en tom liste — er udokumentert.
   */
  uploadedFiles?: ReadonlyMap<string, readonly string[]>;
  /**
   * Kravene søkeren har avgitt erklæring på, som id-er i SMÅ BOKSTAVER.
   *
   * Bygges av `confirmedRequirementIds()` i `declarations.ts`. Små bokstaver er
   * ikke en forenkling: backend lowercaser URI-en før den lagres, så det er alt
   * som finnes å sammenlikne med.
   */
  confirmedRequirements?: ReadonlySet<string>;
  /** Navn å bruke når kravet mangler `label`. */
  titleFallback?: (result: RequirementResult) => string;
  /**
   * Kravtypen på norsk («Sertifikat», «Kurs», «Fartstid»).
   *
   * Brukes til to ting: ledeteksten foran radteksten (`typeLabel` på noden), og
   * til å kjenne igjen begrunnelser som gjentar kravets navn og status med
   * andre ord — se `restatesRow()`. Uten den får ingen rad ledetekst, og flere
   * begrunnelser blir stående; aldri færre.
   */
  requirementTypeLabel?: (requirementType: string) => string;
}

/** Det byggeren trenger internt, med alle valgfrie felter fylt ut. */
interface BuildContext {
  texts: UserViewTexts;
  translate: (langMap?: LangMap | null) => string;
  uploadedFiles: ReadonlyMap<string, readonly string[]>;
  confirmedRequirements: ReadonlySet<string>;
  titleFallback: (result: RequirementResult) => string;
  requirementTypeLabel: (requirementType: string) => string;
  /** Ordforrådet i statusetikettene: «mangler», «oppfylt», «dokumentert», … */
  statusWords: Set<string>;
}

/** Ingen filer — én delt, frossen liste, så tomme noder ikke lager søppel. */
const NO_FILES: readonly string[] = Object.freeze([]);

/** Filene som ligger på kravet med denne id-en. */
const filesFor = (requirementId: string | undefined, context: BuildContext): readonly string[] =>
  (requirementId && context.uploadedFiles.get(requirementId)) || NO_FILES;

/* ------------------------------------------------------------------ */
/* Gjenkjenning av gruppetyper                                          */
/* ------------------------------------------------------------------ */

/**
 * Barna i én gren, sortert på `order`.
 *
 * Sorteringen er Min side sitt tillegg til den porterte modellen: `order` er
 * kravdokumentets egen visningsrekkefølge, og den forrige kravsiden fulgte den.
 * Sorteringen er stabil, så krav uten `order` beholder motorens rekkefølge.
 */
const children = (
  result: RequirementResult,
  key: 'allOf' | 'anyOf' | 'oneOf'
): RequirementResult[] => sortRequirements(result[key] || []);

/** Noden bærer et konkret krav, ikke bare en operator. */
const isLeaf = (result: RequirementResult): boolean =>
  typeof result.requirementType === 'string' && result.requirementType.length > 0;

/**
 * En gren som selv er sammensatt — flere krav som til sammen utgjør en vei.
 * Et enkelt krav (eller et tomt skall) er ikke en kvalifiseringsvei.
 */
const isCompositeBranch = (result: RequirementResult): boolean =>
  !isLeaf(result) &&
  children(result, 'allOf').length +
    children(result, 'anyOf').length +
    children(result, 'oneOf').length >
    0;

/**
 * Er gruppen de alternative kvalifiseringsveiene?
 *
 * `isAlternativeGroup` er flagget skjemaet har for nettopp dette, og brukes når
 * det er satt. Motoren kopierer det ikke alltid inn i valideringsresultatet, så
 * i praksis treffer reserveregelen: en ren gruppe hvis eneste innhold er en
 * `anyOf` med minst to grener, og **der ingen gren bare er ett krav**.
 *
 * Det siste leddet er nødvendig. Vanlige eller-krav ligger også på rotnivå som
 * rene `anyOf`-grupper — «GMDSS: GOC eller ROC», «Sikkerhetskurs: VSK, VSKR,
 * OGD, …» — og uten kravet om sammensatte grener ble hver av dem tegnet som en
 * egen alternativramme, gjentatt flere ganger per sertifikat, med ett enkelt
 * krav som hele «veien». En kvalifiseringsvei er alltid sammensatt (utdanning +
 * fartstid + sertifikat), aldri én enkelt kurskode.
 */
export const isAlternativeGroupResult = (result: RequirementResult): boolean => {
  if (typeof result.isAlternativeGroup === 'boolean') {
    return result.isAlternativeGroup;
  }

  const anyOf = children(result, 'anyOf');

  return (
    !isLeaf(result) &&
    anyOf.length > 1 &&
    children(result, 'allOf').length === 0 &&
    children(result, 'oneOf').length === 0 &&
    anyOf.every(isCompositeBranch)
  );
};

/* ------------------------------------------------------------------ */
/* Tekst                                                                */
/* ------------------------------------------------------------------ */

const textOf = (value: string | null | undefined): string | undefined => {
  const trimmed = (value || '').trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

/**
 * Navnet på et krav. Rekkefølgen er «bruk dataenes egen tekst først»:
 * `label` → kallerens reserve (kravtypen på norsk) → kravets id → «Krav».
 * Vi dikter aldri opp en forklaring — mangler teksten, sier vi så lite som mulig.
 */
const titleOf = (result: RequirementResult, context: BuildContext): string =>
  textOf(context.translate(result.label)) ||
  textOf(context.titleFallback(result)) ||
  textOf(result.requirementId) ||
  context.texts.untitledRequirement;

/**
 * Teksten raden viser.
 *
 * Et enkeltkrav viser beskrivelsen sin — den er skrevet for søkeren, mens
 * navnet er kildedataenes stikkord — og faller tilbake på navnet når kravet
 * ikke har noen beskrivelse. Grupper og alternativer viser navnet sitt; der er
 * beskrivelsen en egen linje under, ikke en erstatning.
 *
 * Ledeteksten (`typeLabel`, «Fartstid: …») er **ikke** med her. Den er en egen
 * opplysning som presentasjonen setter foran teksten, og alt som måler seg mot
 * det brukeren leser — kollaps, arv, begrunnelser, tilgjengelige navn på
 * knappene — skal måle mot kravets egen tekst, ikke mot kravtypen foran den.
 */
export const nodeText = (node: UserNode): string =>
  node.kind === 'requirement' ? node.description || node.title : node.title;

/* ------------------------------------------------------------------ */
/* Gjentakelser                                                         */
/* ------------------------------------------------------------------ */

/*
 * Kildedataene sier ofte det samme flere ganger: en gruppe med samme etikett
 * som sitt eneste krav, en beskrivelse som gjentar etiketten, en begrunnelse
 * som skriver om navnet og statusen. Reglene under fjerner det.
 */

/**
 * Tekst gjort sammenliknbar: små bokstaver, og alt som ikke er bokstav eller
 * siffer blir mellomrom. «GOC and Valid» og «goc  and valid.» blir like, mens
 * to virkelig ulike tekster forblir ulike.
 */
const normalizeText = (value?: string | null): string =>
  (value || '')
    .toLocaleLowerCase('no')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

/** Er de to tekstene praktisk talt den samme? Tom tekst er aldri lik noe. */
const sameText = (left?: string, right?: string): boolean => {
  const normalized = normalizeText(left);
  return normalized.length > 0 && normalized === normalizeText(right);
};

const wordsOf = (value?: string): string[] =>
  normalizeText(value)
    .split(' ')
    .filter((word) => word.length > 0);

/**
 * Bærer ordet informasjon? Ett- og tobokstavsord er bindeord («og», «av»,
 * «på», «er»), mens alt med siffer teller uansett lengde — «5» i «maks tillatt
 * 5» er nettopp poenget med setningen.
 */
const isSignificantWord = (word: string): boolean => word.length > 2 || /\d/.test(word);

/** Ordforrådet i statusetikettene, bygget fra de oversatte tekstene. */
const statusWordsOf = (texts: UserViewTexts): Set<string> => {
  const words = new Set<string>();
  Object.keys(texts.statusTexts).forEach((status) => {
    wordsOf(texts.statusTexts[status as UserStatus]).forEach((word) => words.add(word));
  });
  return words;
};

/**
 * Sier begrunnelsen noe raden ikke allerede sier?
 *
 * Regelen er ordbasert, ikke en liste over kjente strenger: begrunnelsen
 * undertrykkes bare hvis **hvert eneste meningsbærende ord** i den allerede
 * står i radens synlige tekst, i statusordforrådet eller i kravtypens navn. Da
 * er den per definisjon en omskriving av det brukeren ser ved siden av.
 *
 * «Mangler sertifikat GOC» ved kravet «GOC and Valid» med statusen «Mangler»:
 * «mangler» er et statusord, «sertifikat» er kravtypen, «goc» står i navnet —
 * ingenting nytt, den utelates. «Mangler 540 dager fartstid» beholder «540» og
 * «dager», og «Fullført for 6,2 år siden, maks tillatt 5» beholder både tallene
 * og «fullført» — de blir stående.
 *
 * Regelen er robust fordi den er konservativ i riktig retning: ett ukjent ord
 * er nok til å beholde begrunnelsen.
 */
const restatesRow = (
  context: BuildContext,
  reason: string,
  ...covered: (string | undefined)[]
): boolean => {
  const known = new Set(context.statusWords);
  covered.forEach((text) => wordsOf(text).forEach((word) => known.add(word)));

  const words = wordsOf(reason).filter(isSignificantWord);
  return words.length === 0 || words.every((word) => known.has(word));
};

/**
 * Begrunnelsen slik den skal vises — eller ingenting.
 *
 * Den utelates i to tilfeller: når kravet ikke lenger venter på brukeren
 * (oppfylt, dokumentert eller uaktuelt — da motsier «Mangler sertifikat GOC»
 * statusen i stedet for å utfylle den), og når den bare gjentar raden.
 */
const meaningfulReason = (
  context: BuildContext,
  reason: string | undefined,
  status: UserStatus,
  ...covered: (string | undefined)[]
): string | undefined => {
  if (!reason || !isOutstanding(status)) return undefined;
  return restatesRow(context, reason, ...covered) ? undefined : reason;
};

/**
 * Beskrivelsen, med mindre den bare er navnet en gang til.
 *
 * Regelen gjelder **grupper og alternativer**, der navnet står på linja over:
 * to like linjer etter hverandre er støy. Den gjelder ikke enkeltkrav — der er
 * beskrivelsen selve radteksten og navnet vises ikke.
 */
const meaningfulDescription = (
  description: string | undefined,
  title: string
): string | undefined => (description && !sameText(description, title) ? description : undefined);

/* ------------------------------------------------------------------ */
/* Kravtypen som ledetekst                                              */
/* ------------------------------------------------------------------ */

/**
 * Begynner teksten med akkurat disse ordene?
 *
 * Sammenlikningen er den samme normaliserte som resten av gjentakelsesreglene,
 * og krever **ordgrense**: «Sertifikat» begynner «Sertifikat for maskinsjef»,
 * men ikke «Sertifikatkrav for maskinsjef», og aller minst
 * «Kompetansesertifikat …».
 */
const startsWithText = (text: string, prefix: string): boolean => {
  const normalized = normalizeText(text);
  const start = normalizeText(prefix);

  return start.length > 0 && (normalized === start || normalized.startsWith(`${start} `));
};

/**
 * Ledeteksten på et enkeltkrav: kravtypen på norsk, eller ingenting.
 *
 * Den utgår når kravet ikke har noen type å slå opp, når kalleren ikke har gitt
 * oss en katalog — og når **teksten allerede begynner med typenavnet**. Ellers
 * ville raden hete «Sertifikat: Sertifikat for maskinsjef», som er samme
 * dobling reglene lenger oppe fjerner fra begrunnelsene.
 */
const typeLabelFor = (
  requirementType: string | undefined,
  text: string,
  context: BuildContext
): string | undefined => {
  const label = requirementType ? textOf(context.requirementTypeLabel(requirementType)) : undefined;
  if (!label) return undefined;

  return startsWithText(text, label) ? undefined : label;
};

/**
 * Hvor mange ulike kravtyper en gruppe kan liste før ledeteksten utgår.
 *
 * «Kurs / Sertifikat: …» sier fortsatt noe konkret. En gruppe som blander tre
 * eller flere typer gjør det ikke — da blir ledeteksten en oppramsing som er
 * lengre enn kravet den skal lede.
 */
const MAX_GROUP_TYPES = 2;

/**
 * Ledeteksten på en gruppe, utledet av kravene under den.
 *
 * Gruppen har ingen egen kravtype — den er en operator, ikke et krav — men
 * kravene inni har det, og som regel samme: «minst ett av disse
 * sertifikatene». Da er «Sertifikat» nettopp det gruppen handler om.
 *
 * Regelen er derfor: **alle** kravene under gruppen må ha et typenavn, og de må
 * være høyst `MAX_GROUP_TYPES` ulike. Mangler ett av dem typen, eller spriker de
 * for mye, utgår ledeteksten — vi gjetter ikke på hva gruppen «egentlig»
 * gjelder. Rekkefølgen er den kravene står i, ikke alfabetisk.
 */
const groupTypeLabel = (
  items: UserNode[],
  text: string,
  context: BuildContext
): string | undefined => {
  const rows = collectRequirements(items);
  if (rows.length === 0) return undefined;

  const labels: string[] = [];

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    const label = row.requirementType
      ? textOf(context.requirementTypeLabel(row.requirementType))
      : undefined;
    // Ett krav uten typenavn er nok: gruppen inneholder noe vi ikke kan
    // beskrive, og da skal ledeteksten ikke påstå at den kan.
    if (!label) return undefined;
    if (labels.indexOf(label) < 0) labels.push(label);
    if (labels.length > MAX_GROUP_TYPES) return undefined;
  }

  const combined = labels.join(' / ');
  return startsWithText(text, combined) ? undefined : combined;
};

/**
 * Slår en gruppe sammen med sitt eneste barn: barnet tegnes direkte, uten
 * wrapper. Returnerer `undefined` når sammenslåingen ville skjult noe, og
 * gruppen derfor må bli stående.
 *
 * Det som kan gå tapt, følger med eller stopper kollapsen:
 *   - **Status**: skiller gruppens status seg fra barnets, sier de to nivåene
 *     faktisk noe ulikt, og gruppen beholdes.
 *   - **Beskrivelse**: har gruppen en beskrivelse barnet mangler, arver barnet
 *     den. Har barnet sin egen, vinner den — og er gruppens beskrivelse bare
 *     barnets navn om igjen, droppes den.
 *
 * Gruppens *etikett* følger ikke med. Den er en teknisk gruppering i
 * kildedataene («VYD_3» over kravet «VYD»), og kravets eget navn er alltid det
 * mer presise av de to.
 */
const collapseWrapper = (
  child: UserNode,
  wrapper: { description?: string; status: UserStatus },
  context: BuildContext
): UserNode | undefined => {
  if (wrapper.status !== child.status) return undefined;
  if (!wrapper.description || child.description) return child;
  if (sameText(wrapper.description, child.title)) return child;
  return withInheritedDescription(child, wrapper.description, context);
};

/**
 * Lar noden arve gruppens beskrivelse.
 *
 * På et enkeltkrav blir den arvede teksten radens synlige tekst, og da må
 * begrunnelsen prøves på nytt: den ble målt mot navnet, som raden ikke lenger
 * viser. Prøven er den samme som ved bygging av kravet — den nye teksten,
 * statusordene og kravtypen.
 */
const withInheritedDescription = (
  child: UserNode,
  description: string,
  context: BuildContext
): UserNode => {
  if (child.kind !== 'requirement') return { ...child, description };

  const typeLabel = child.requirementType
    ? textOf(context.requirementTypeLabel(child.requirementType))
    : undefined;

  return {
    ...child,
    description,
    // Ledeteksten måles mot den teksten raden viser, og raden viser nå en
    // annen: arver kravet en beskrivelse som allerede begynner med typenavnet,
    // skal ledeteksten falle bort — og motsatt.
    typeLabel: typeLabelFor(child.requirementType, description, context),
    reason:
      child.reason &&
      restatesRow(context, child.reason, description, typeLabel, child.requirementType)
        ? undefined
        : child.reason,
  };
};

/* ------------------------------------------------------------------ */
/* Opplasting: hvem bærer knappen                                       */
/* ------------------------------------------------------------------ */

/*
 * Én knapp per dokumentasjonsenhet.
 *
 * Søkeren leverer sjelden ett papir per krav. En gruppe — «minst ett av disse
 * sertifikatene», «alle disse kravene» — er én ting å dokumentere, og skal ha
 * én knapp; kravene under er forklaringen på hva gruppen krever. Derfor:
 *
 *   - En **gruppe** (bunt eller valg) bærer knappen, og kravene under den har
 *     ingen. Ligger en gruppe inni en annen gruppe, er det den ytterste som
 *     bærer den.
 *   - Et **enkeltkrav utenfor enhver gruppe** bærer sin egen knapp. Det gjelder
 *     både kravene rett på flaten i basisseksjonen og kravene som ligger rett i
 *     en alternativboks.
 *   - **Alternativgruppen og alternativboksene** har ingen knapp. Å «laste opp
 *     for hele valget» gir ikke mening: gruppen er et valg mellom veier.
 */

/** Barna til en gruppe, uansett om den er en bunt eller et valg. */
const groupItems = (node: UserBundleNode | UserChoiceNode): UserNode[] =>
  node.kind === 'bundle' ? node.items : node.options;

/**
 * Finnes det noe å laste opp under noden?
 *
 * Et krav teller når det både *kan* dokumenteres (`documentable` — alderskrav
 * og andre automatisk vurderte krav kan ikke) og fortsatt *venter*: det mangler,
 * eller det er allerede dokumentert (og kan da suppleres). Oppfylte og uaktuelle
 * krav ber ikke om noe.
 *
 * En gruppe teller når minst ett krav under den gjør det. Det avgjør
 * blandingstilfellet: en gruppe der noen krav vurderes automatisk og andre må
 * dokumenteres, beholder knappen — ellers ville de dokumenterbare kravene bli
 * umulige å levere for, siden de ikke har egen knapp.
 *
 * En ERKLÆRING teller aldri: den besvares med en avkryssing, ikke med en fil.
 * Kravdokumentet sender riktignok `documentable: false` på typen, men regelen
 * står her i tillegg — søkeren skal aldri bes om å laste opp et papir for en
 * avkryssing, heller ikke om flagget skulle mangle i dataene.
 */
const hasPendingUpload = (node: UserNode): boolean =>
  node.kind === 'requirement'
    ? node.documentable &&
      !node.declaration &&
      (isOutstanding(node.status) || node.status === 'uploaded')
    : groupItems(node).some(hasPendingUpload);

/**
 * Setter `upload` på noden og alle etterkommere.
 *
 * `insideGroup` er «står noden allerede inni noe som dokumenteres under ett».
 * Den slås på så snart vi går ned i en gruppe, og er hele mekanismen bak at
 * bare det ytterste nivået får knappen.
 */
const assignUpload = (node: UserNode, insideGroup: boolean): UserNode => {
  const upload = !insideGroup && hasPendingUpload(node);

  if (node.kind === 'requirement') return { ...node, upload };

  const items = groupItems(node).map((child) => assignUpload(child, true));

  return node.kind === 'bundle' ? { ...node, upload, items } : { ...node, upload, options: items };
};

/**
 * Fordeler opplastingsknappene over en liste noder som står fritt — rett på
 * flaten i basisseksjonen, eller rett i en alternativboks.
 */
const withUploadControls = (nodes: UserNode[]): UserNode[] =>
  nodes.map((node) => assignUpload(node, false));

/**
 * Gruppens status etter at en eventuell gruppeopplasting er tatt med.
 *
 * Opplastingen virker på gruppen selv, akkurat som på et enkeltkrav: venter
 * gruppen på brukeren, blir den «Dokumentert». Kravene under blir stående som
 * motoren fant dem — vi vet ikke hvilke av dem dokumentet dekker, og på et
 * «minst ett av»-valg ville det å merke alle som dokumentert påstå at brukeren
 * har levert dokumentasjon for hver eneste vei.
 */
const groupStatus = (
  aggregate: UserStatus,
  requirementId: string | undefined,
  context: BuildContext
): UserStatus => withUpload(aggregate, filesFor(requirementId, context).length > 0);

/**
 * Hvordan kravene under gruppen henger sammen, som en ledetekst.
 *
 * Teksten hører til den **ekspanderte** gruppen: kollapset er gruppen én ting,
 * og logikken sier søkeren ingenting. Er kravene hentet fram, er den derimot
 * nettopp det hun trenger — må hun ha alle, eller holder det med ett?
 */
export const groupLogicText = (
  node: UserBundleNode | UserChoiceNode,
  texts: UserViewTexts
): string => {
  if (node.kind === 'bundle') return texts.groupLogicAllOf;
  return node.exclusive ? texts.groupLogicExactlyOneOf : texts.groupLogicAtLeastOneOf;
};

/* ------------------------------------------------------------------ */
/* Bygging av noder                                                     */
/* ------------------------------------------------------------------ */

const requirementNode = (
  result: RequirementResult,
  key: string,
  context: BuildContext
): UserRequirementNode => {
  const title = titleOf(result, context);
  // Beskrivelsen tas rå: den er radens tekst, og skal stå selv om den likner
  // navnet — navnet vises jo ikke ved siden av den.
  const description = textOf(context.translate(result.description));
  const files = filesFor(result.requirementId, context);
  const requirementType = result.requirementType || undefined;
  /*
   * Erklæringer vurderes ikke av motoren — den svarer alltid «vurderes utenfor
   * motoren». Er erklæringen avgitt på søknaden, er kravet oppfylt, og
   * motorsvaret skal ikke få si noe annet.
   */
  const declaration = isDeclarationRequirement(requirementType);
  const confirmed =
    declaration && isRequirementConfirmed(context.confirmedRequirements, result.requirementId);
  const status = confirmed ? 'fulfilled' : mapEngineStatus(result.status, files.length > 0);
  const typeLabel = requirementType
    ? textOf(context.requirementTypeLabel(requirementType))
    : undefined;
  // Teksten raden faktisk viser — både begrunnelsen og ledeteksten måles mot
  // den, ikke mot navnet visningen eventuelt har byttet bort.
  const text = description || title;

  return {
    kind: 'requirement',
    key,
    requirementId: result.requirementId || undefined,
    title,
    label: result.label,
    description,
    typeLabel: typeLabelFor(requirementType, text, context),
    reason: meaningfulReason(
      context,
      textOf(result.reason),
      status,
      text,
      typeLabel,
      requirementType
    ),
    status,
    // Motorens eget svar, uoversatt — se `engineStatus` i UserNodeBase.
    engineStatus: normalizeStatus(result.status),
    requirementType,
    // Utelatt `documentable` betyr true — kun automatisk vurderte krav
    // (alder m.m.) sendes med false.
    documentable: result.documentable !== false,
    declaration,
    confirmed,
    order: typeof result.order === 'number' ? result.order : undefined,
    // Avgjøres av `withUploadControls()` når treet er ferdig.
    upload: false,
    files,
  };
};

/**
 * Gjør én resultatnode om til noder i en «alle må oppfylles»-liste.
 *
 * Blad-krav blir én rad. Rene grupper flates ut: `allOf`-barna legges rett inn i
 * lista (gruppens egen etikett er en teknisk gruppering, ikke noe søkeren
 * trenger), mens `anyOf`/`oneOf` blir en egen valggruppe. Nestede underkrav på
 * et blad-krav vises ikke — kravets egen status og begrunnelse dekker dem.
 */
const toNodes = (result: RequirementResult, key: string, context: BuildContext): UserNode[] => {
  if (isLeaf(result)) return [requirementNode(result, key, context)];

  const allOf = children(result, 'allOf');
  const anyOf = children(result, 'anyOf');
  const oneOf = children(result, 'oneOf');

  // Ren gruppe uten barn: dinglende shape eller tomt skall. Vis den som en rad
  // med motorens begrunnelse, så kravet ikke forsvinner stille.
  if (allOf.length === 0 && anyOf.length === 0 && oneOf.length === 0) {
    return [requirementNode(result, key, context)];
  }

  const nodes: UserNode[] = [];

  allOf.forEach((child, index) => {
    nodes.push(...toNodes(child, `${key}/allOf.${index}`, context));
  });

  if (anyOf.length > 0) {
    nodes.push(choiceNode(result, anyOf, 'anyOf', key, context));
  }
  if (oneOf.length > 0) {
    nodes.push(choiceNode(result, oneOf, 'oneOf', key, context));
  }

  return nodes;
};

/**
 * Ett valg i en gruppe. Består valget av flere krav, samles de i en bunt —
 * består det av ett, tegnes kravet direkte (bunten ville bare gjentatt navnet
 * og statusen på en linje til).
 */
const optionNode = (
  result: RequirementResult,
  key: string,
  index: number,
  context: BuildContext
): UserNode => {
  const items = toNodes(result, key, context);
  const title =
    textOf(context.translate(result.label)) || context.texts.alternativeFallback(index + 1);
  const description = meaningfulDescription(textOf(context.translate(result.description)), title);
  // Et valg helt uten krav under seg er ikke noe vi kan si er oppfylt: det
  // faller tilbake på «Mangler», slik ukjente motorsvar gjør.
  const aggregate = aggregateAll(items.map((item) => item.status)) || 'missing';

  // Kollapsen måles mot kravenes egen status: en opplasting på bunten skal ikke
  // avgjøre om bunten i det hele tatt finnes.
  if (items.length === 1) {
    const collapsed = collapseWrapper(items[0], { description, status: aggregate }, context);
    if (collapsed) return collapsed;
  }

  const status = groupStatus(aggregate, result.requirementId, context);

  return {
    kind: 'bundle',
    key,
    requirementId: result.requirementId || undefined,
    title,
    label: result.label,
    description,
    // Bunten har ingen egen kravtype — den låner den av kravene sine.
    typeLabel: groupTypeLabel(items, title, context),
    reason: meaningfulReason(context, textOf(result.reason), status, title),
    status,
    items,
    upload: false,
    files: filesFor(result.requirementId, context),
  };
};

/**
 * «Minst ett av»-gruppen. Ett eneste valg er ikke et valg: da faller gruppen
 * bort og kravet står alene, med gruppens beskrivelse arvet hvis kravet ikke
 * har sin egen.
 */
const choiceNode = (
  parent: RequirementResult,
  branch: RequirementResult[],
  operator: 'anyOf' | 'oneOf',
  key: string,
  context: BuildContext
): UserNode => {
  const options = branch.map((child, index) =>
    optionNode(child, `${key}/${operator}.${index}`, index, context)
  );

  const aggregate = aggregateAny(options.map((option) => option.status)) || 'missing';

  // Gruppens egen etikett står som reserve for beskrivelsen.
  const ownDescription = textOf(context.translate(parent.description));
  const description = ownDescription || textOf(context.translate(parent.label));

  if (options.length === 1) {
    /*
     * Bare gruppens ekte beskrivelse arves ned på det ene valget. Etiketten er
     * en teknisk gruppering («Radiosertifikat» over kravet «GOC»), og siden
     * arven blir enkeltkravets synlige tekst, ville den fortrengt kravets eget
     * navn i stedet for å utfylle det.
     */
    const collapsed = collapseWrapper(
      options[0],
      { description: ownDescription, status: aggregate },
      context
    );
    if (collapsed) return collapsed;
  }

  /*
   * Gruppens tekst er beskrivelsen — den som forteller hva valget gjelder
   * («Gyldig ROC eller GOC, STCW regel IV/2.»), eller etiketten når det er alt
   * dataene har. Logikken («Minst ett av») står ikke som en egen merkelapp: den
   * sier ingenting søkeren trenger, og gjentok seg nedover hele siden. Bare når
   * dataene ikke har noen tekst i det hele tatt, brukes den som reserve.
   */
  const title =
    description || (operator === 'oneOf' ? context.texts.exactlyOneOf : context.texts.atLeastOneOf);

  const groupKey = `${key}/${operator}`;

  return {
    kind: 'choice',
    key: groupKey,
    // Gruppens egen id — den er kravnøkkelen en gruppeopplasting henger på.
    requirementId: parent.requirementId || undefined,
    title,
    label: parent.label,
    // Valget låner kravtypen av valgene sine — «minst ett av disse
    // sertifikatene» er et sertifikatkrav for søkeren.
    typeLabel: groupTypeLabel(options, title, context),
    status: groupStatus(aggregate, parent.requirementId, context),
    exclusive: operator === 'oneOf',
    // Nedtoningen måles mot kravenes egen status: en opplasting på gruppen
    // peker ikke ut hvilket valg brukeren har gått for.
    options: withUnusedOptionsGreyed(
      options,
      aggregate,
      branch.map((child) => child.informative === true)
    ),
    upload: false,
    files: filesFor(parent.requirementId, context),
  };
};

/* ------------------------------------------------------------------ */
/* «Ikke aktuelt» — veien brukeren ikke følger                          */
/* ------------------------------------------------------------------ */

/**
 * Setter status på noden og alle etterkommere. Begrunnelsen faller bort på
 * veien: «Mangler sertifikat GOC» ved siden av «Ikke aktuelt» sier to ting som
 * ikke henger sammen.
 */
const greyOut = (node: UserNode): UserNode => {
  // Opplastingen faller bort sammen med begrunnelsen: det er ingenting å levere
  // for en vei brukeren ikke følger, og filene hun eventuelt la ved hører til
  // den veien hun faktisk er på.
  if (node.kind === 'requirement') {
    return {
      ...node,
      status: 'notApplicable',
      reason: undefined,
      upload: false,
      files: NO_FILES,
    };
  }
  if (node.kind === 'bundle') {
    return {
      ...node,
      status: 'notApplicable',
      reason: undefined,
      upload: false,
      files: NO_FILES,
      items: node.items.map(greyOut),
    };
  }
  return {
    ...node,
    status: 'notApplicable',
    reason: undefined,
    upload: false,
    files: NO_FILES,
    options: node.options.map(greyOut),
  };
};

/**
 * Statusene som betyr at brukeren har landet på denne veien: den er enten
 * kontrollert og godkjent, eller så har hun levert det som skulle til og venter
 * på svar. Begge deler gjør de øvrige veiene uaktuelle.
 */
const isSettled = (status: UserStatus): boolean => status === 'fulfilled' || status === 'uploaded';

/**
 * Hvilket valg som bærer gruppens status — det brukeren er på.
 *
 * Motoren vurderer alle valgene i en eller-gruppe, også dem etter veien den
 * valgte, og merker de sene med `informative`. Siden `order` ikke følger
 * motorens rekkefølge, kan et slikt valg stå først med samme status som veien
 * motoren faktisk valgte. Da vinner det ikke-informative valget.
 *
 * Et informativt valg pekes ALDRI ut (beslutning 2026-09-28): det er ikke veien
 * motoren kvalifiserte brukeren på. Har ingen ikke-informative valg statusen,
 * er svaret -1 — ingen vinner, som når ingen vei er i havn. Uten feltet (eldre
 * motor) er dette det første valget med statusen, som før.
 */
const carrierIndex = (
  statuses: UserStatus[],
  informative: readonly boolean[],
  status: UserStatus
): number => statuses.findIndex((s, index) => s === status && !informative[index]);

/**
 * Er ett av valgene landet — oppfylt eller dokumentert — er de øvrige ikke
 * lenger aktuelle. Er ingen kommet så langt, står alle med sin egen status, for
 * da er alle fortsatt mulige veier. Det samme gjelder når bare et informativt
 * valg har statusen: da er ingen vinner pekt ut, og ingenting tones ned.
 */
const withUnusedOptionsGreyed = (
  options: UserNode[],
  aggregate: UserStatus,
  informative: readonly boolean[]
): UserNode[] => {
  if (!isSettled(aggregate)) return options;

  const keep = carrierIndex(
    options.map((option) => option.status),
    informative,
    aggregate
  );
  if (keep < 0) return options;
  return options.map((option, index) => (index === keep ? option : greyOut(option)));
};

/* ------------------------------------------------------------------ */
/* Alternativkortene                                                    */
/* ------------------------------------------------------------------ */

/** Alle krav-radene i et deltre, i visningsrekkefølge. */
export const collectRequirements = (nodes: UserNode[]): UserRequirementNode[] => {
  const found: UserRequirementNode[] = [];

  const walk = (node: UserNode) => {
    if (node.kind === 'requirement') found.push(node);
    else groupItems(node).forEach(walk);
  };

  nodes.forEach(walk);
  return found;
};

/**
 * Kravtypen noden dokumenterer, rå som den står i kravdokumentet.
 *
 * Et enkeltkrav har sin egen. En gruppe er en operator og har ingen — men
 * kravene under den har det, og er de alle av samme type, er det nettopp den
 * typen gruppen ber om dokumentasjon på («minst ett av disse sertifikatene»).
 * Spriker de, eller mangler ett av dem typen, svarer vi `undefined`: vi gjetter
 * ikke på hva gruppen «egentlig» gjelder. Samme forsiktighet som
 * `groupTypeLabel()`, bare strengere — her holder det ikke med to typer, for
 * svaret brukes til å velge behandling, ikke til å skrive en ledetekst.
 */
export const nodeRequirementType = (node: UserNode): string | undefined => {
  if (node.kind === 'requirement') return node.requirementType;

  const types = collectRequirements([node]).map((row) => row.requirementType);
  if (types.length === 0) return undefined;
  return types.every((type) => !!type && type === types[0]) ? types[0] : undefined;
};

/**
 * Kravene brukeren fortsatt må følge opp — de som mangler.
 *
 * En gruppe som er dokumentert under ett regnes som besvart, og vi går ikke ned
 * i den: brukeren har levert det gruppen ba om, og kravene under er ikke lenger
 * noe hun må gjøre noe med — selv om motoren fortsatt sier de mangler.
 */
export const outstandingRequirements = (nodes: UserNode[]): UserRequirementNode[] => {
  const found: UserRequirementNode[] = [];

  const walk = (node: UserNode) => {
    if (node.kind === 'requirement') {
      if (isOutstanding(node.status)) found.push(node);
      return;
    }
    if (node.status === 'uploaded') return;
    groupItems(node).forEach(walk);
  };

  nodes.forEach(walk);
  return found;
};

const buildAlternative = (
  result: RequirementResult,
  key: string,
  index: number,
  context: BuildContext
): UserAlternative => {
  // Kravene ligger fritt i boksen: de som ikke er pakket i en gruppe bærer sin
  // egen knapp, mens en gruppe i boksen dokumenteres under ett.
  const items = withUploadControls(toNodes(result, key, context));
  const label = textOf(context.translate(result.label)) || textOf(context.titleFallback(result));
  const title = label || context.texts.alternativeFallback(index + 1);
  // Alternativet er én komplett vei: alle kravene i det må være oppfylt.
  const status = aggregateAll(items.map((item) => item.status)) || 'missing';
  const description = meaningfulDescription(textOf(context.translate(result.description)), title);

  /*
   * Består alternativet av ett eneste krav, står navnet og statusen allerede på
   * kravraden, og alternativets egen topplinje ville sagt begge deler om igjen.
   *
   * Topplinja blir stående så snart den har noe eget å si: en beskrivelse, en
   * status som skiller seg fra kravets, eller et navn fra kildedataene som ikke
   * er kravets navn om igjen. Er navnet bare reservenavnet «Alternativ N»,
   * teller det ikke som noe eget — nummeret er vår egen oppfinnelse.
   *
   * Navnet måles mot radens synlige tekst, ikke mot kravets navn: viser raden
   * kravets beskrivelse, er alternativets etikett det eneste stedet navnet
   * ville stått, og da skal topplinja bli.
   */
  const single = items.length === 1 ? items[0] : undefined;
  const collapsed =
    single !== undefined &&
    single.status === status &&
    description === undefined &&
    (label === undefined || sameText(label, nodeText(single)));

  return {
    key,
    title,
    description,
    status,
    items,
    missing: outstandingRequirements(items),
    collapsed,
  };
};

const buildAlternativeGroup = (
  result: RequirementResult,
  key: string,
  context: BuildContext
): UserAlternativeGroup => {
  const sources = children(result, 'anyOf');
  const built = sources.map((child, index) =>
    buildAlternative(child, `${key}/anyOf.${index}`, index, context)
  );

  // Gruppestatusen regnes ut FØR nedtoningen, ellers ville «ikke aktuelt»
  // spist opp den oppfylte veien.
  const status = aggregateAny(built.map((alternative) => alternative.status)) || 'missing';

  /*
   * Veien brukeren er på. Gruppestatusen er den beste av alternativene, så den
   * peker samtidig ut hvilken status det er som bærer: er den `fulfilled`, er
   * det en oppfylt vei; er den `uploaded`, er det en hun har dokumentert.
   * Har flere den statusen, vinner veien motoren valgte framfor et informativt
   * alternativ, og et informativt alternativ pekes aldri ut — se
   * `carrierIndex()`. Da er indeksen -1, og ingen vei tones ned.
   */
  const highlightIndex = isSettled(status)
    ? carrierIndex(
        built.map((alternative) => alternative.status),
        sources.map((child) => child.informative === true),
        status
      )
    : -1;

  const alternatives = built.map((alternative, index) => {
    if (highlightIndex < 0 || index === highlightIndex) return alternative;
    const items = alternative.items.map(greyOut);
    return { ...alternative, status: 'notApplicable' as const, items, missing: [] };
  });

  return {
    key,
    // Overskriften er den faste brukerteksten, ikke gruppens egen etikett:
    // emitteren gir alltid gruppen den samme tekniske teksten («Alternative
    // veier (én må oppfylles)»), som sier det samme dårligere.
    title: context.texts.alternativesHeading,
    description: textOf(context.translate(result.description)),
    status,
    alternatives,
    highlightIndex,
  };
};

/* ------------------------------------------------------------------ */
/* Inngangen                                                            */
/* ------------------------------------------------------------------ */

/**
 * Bygger hele søkervisningen. Kall den på nytt når vedleggene endrer seg —
 * funksjonen er ren, så resultatet kan memoiseres på (resultat, vedlegg).
 */
export const buildUserView = (
  result: ValidationResult,
  options: BuildUserViewOptions
): UserView => {
  const context: BuildContext = {
    texts: options.texts,
    translate: options.translate,
    uploadedFiles: options.uploadedFiles || new Map<string, readonly string[]>(),
    confirmedRequirements: options.confirmedRequirements || new Set<string>(),
    titleFallback: options.titleFallback || (() => ''),
    requirementTypeLabel: options.requirementTypeLabel || (() => ''),
    statusWords: statusWordsOf(options.texts),
  };

  const { root } = result;
  const collected: UserNode[] = [];
  const alternativeGroups: UserAlternativeGroup[] = [];

  if (root) {
    const topLevel = children(root, 'allOf');

    // Motoren legger alltid kravene under root.allOf. Skulle roten likevel bære
    // kravet selv, vises det heller enn å forsvinne. En tom root.allOf er
    // derimot et sertifikat uten krav, og skal gi en tom visning.
    if (
      topLevel.length === 0 &&
      (isLeaf(root) || children(root, 'anyOf').length > 0 || children(root, 'oneOf').length > 0)
    ) {
      collected.push(...toNodes(root, 'root', context));
    }

    topLevel.forEach((child, index) => {
      const key = `root/allOf.${index}`;
      if (isAlternativeGroupResult(child)) {
        alternativeGroups.push(buildAlternativeGroup(child, key, context));
      } else {
        collected.push(...toNodes(child, key, context));
      }
    });
  }

  // Basiskravene står fritt på flaten: enkeltkravene bærer sin egen knapp, mens
  // en gruppe blant dem dokumenteres under ett.
  const baseItems = withUploadControls(collected);

  const status =
    aggregateAll([
      ...baseItems.map((item) => item.status),
      ...alternativeGroups.map((group) => group.status),
    ]) || 'missing';

  return {
    certificateName: options.certificateName,
    status,
    baseItems,
    alternativeGroups,
  };
};
